import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { LogOut, RefreshCw, Phone, Mail, Trash2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/site/Logo";

const STATUSES = ["new", "confirmed", "in_progress", "completed", "cancelled"];
const STATUS_STYLE = { new: "bg-brass text-ink", confirmed: "bg-cobalt text-white", in_progress: "bg-sky-200 text-ink", completed: "bg-emerald-600 text-white", cancelled: "bg-slate-300 text-slate-700" };

const download = async (r, a) => {
  try {
    const res = await api.get(`/requests/${r.id}/attachments/${a.id}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const link = document.createElement("a");
    link.href = url; link.download = a.filename; link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) { toast.error("Download failed"); }
};

const Attachments = ({ r }) => (Array.isArray(r.attachments) && r.attachments.length > 0 ? (
  <div className="pt-2 space-y-1">
    {r.attachments.map((a, i) => (
      <button key={a.id} type="button" onClick={() => download(r, a)} data-testid={`admin-attachment-${r.reference}-${i}`}
        className="flex items-center gap-2 text-cobalt hover:underline text-left break-all"><Paperclip className="h-3.5 w-3.5 shrink-0" />{a.filename}</button>
    ))}
  </div>
) : null);

const JobCard = ({ r, onStatus, onDelete }) => (
  <div data-testid={`admin-request-${r.reference}`} className="bg-white border border-ink/10 p-5 grid gap-4 lg:grid-cols-12 items-start">
    <div className="lg:col-span-3">
      <p className="font-mono text-xs text-cobalt font-bold">{r.reference}</p>
      <p className="font-wide font-extrabold text-lg mt-1">{r.name}</p>
      <p className="text-xs text-slate-500 mt-1">{new Date(r.created_at).toLocaleString("en-AU")}</p>
    </div>
    <div className="lg:col-span-4 text-sm space-y-1">
      <p><span className={`inline-block px-2 py-0.5 text-[11px] font-mono uppercase mr-2 ${r.kind === "booking" ? "bg-ink text-white" : "bg-bone"}`}>{r.kind}</span><b>{r.service}</b> · {r.property_type}</p>
      {r.date && <p className="text-slate-700">{r.date} · {r.time_slot}</p>}
      <p className="text-slate-600">{r.suburb}{r.address ? `, ${r.address}` : ""} · {r.urgency}</p>
      {r.message && <p className="text-slate-600 italic">"{r.message}"</p>}
      <Attachments r={r} />
    </div>
    <div className="lg:col-span-3 text-sm space-y-1">
      <a href={`tel:${r.phone}`} className="flex items-center gap-2 hover:text-cobalt"><Phone className="h-4 w-4" />{r.phone}</a>
      <a href={`mailto:${r.email}`} className="flex items-center gap-2 hover:text-cobalt break-all"><Mail className="h-4 w-4 shrink-0" />{r.email}</a>
      <p className="text-xs text-slate-500">Email alert: {r.email_status}</p>
    </div>
    <div className="lg:col-span-2 flex lg:flex-col gap-2">
      <select value={r.status} onChange={(e) => onStatus(r.id, e.target.value)} data-testid={`admin-status-${r.reference}`}
        className={`h-10 px-2 text-xs font-bold uppercase ${STATUS_STYLE[r.status]}`}>
        {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
      </select>
      <button onClick={() => onDelete(r.id)} data-testid={`admin-delete-${r.reference}`} className="h-10 px-3 border border-ink/15 text-slate-600 hover:text-red-600 hover:border-red-300 flex items-center justify-center gap-1 text-xs"><Trash2 className="h-4 w-4" />Delete</button>
    </div>
  </div>
);

export default function AdminDashboard() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/requests");
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) { localStorage.removeItem("austyle_admin_token"); nav("/admin/login"); }
      else toast.error(formatApiError(err));
    }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const onStatus = async (id, status) => {
    try { await api.patch(`/requests/${id}`, { status }); setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r))); toast.success("Status updated"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try { await api.delete(`/requests/${id}`); setRows((rs) => rs.filter((r) => r.id !== id)); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const logout = async () => { await api.post("/auth/logout").catch(() => {}); localStorage.removeItem("austyle_admin_token"); nav("/admin/login"); };

  const list = useMemo(() => (rows || []).filter((r) => filter === "all" || r.status === filter || r.kind === filter), [rows, filter]);
  const count = (fn) => (rows || []).filter(fn).length;

  return (
    <div className="min-h-screen bg-porcelain" data-testid="admin-dashboard">
      <Helmet><title>Dashboard | Austyle Plumbing</title><meta name="robots" content="noindex" /></Helmet>
      <header className="bg-ink py-4">
        <div className="container-x flex items-center justify-between">
          <Logo light />
          <button onClick={logout} data-testid="admin-logout" className="text-white/80 hover:text-white flex items-center gap-2 text-sm"><LogOut className="h-4 w-4" />Log out</button>
        </div>
      </header>
      <div className="container-x py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-wide font-black uppercase text-3xl sm:text-4xl">Job requests</h1>
          <button onClick={load} data-testid="admin-refresh" className="h-10 px-4 border border-ink/20 flex items-center gap-2 text-sm font-bold hover:bg-white"><RefreshCw className="h-4 w-4" />Refresh</button>
        </div>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
          {[["Total", count(() => true)], ["New", count((r) => r.status === "new")], ["Bookings", count((r) => r.kind === "booking")], ["Quotes", count((r) => r.kind === "quote")]].map(([l, n]) => (
            <div key={l} className="bg-white p-5" data-testid={`admin-metric-${l.toLowerCase()}`}><p className="eyebrow text-slate-500">{l}</p><p className="font-wide font-black text-4xl mt-2">{n}</p></div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-2">
          {["all", "booking", "quote", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`admin-filter-${s}`} className={`h-9 px-4 rounded-full text-xs font-bold uppercase border ${filter === s ? "bg-ink text-white border-ink" : "border-ink/20 hover:border-ink"}`}>{s.replace("_", " ")}</button>
          ))}
        </div>
        <div className="mt-6 space-y-3">
          {rows === null && <p className="text-slate-500">Loading…</p>}
          {rows !== null && list.length === 0 && <p data-testid="admin-empty" className="text-slate-500 py-10">No requests yet.</p>}
          {list.map((r) => <JobCard key={r.id} r={r} onStatus={onStatus} onDelete={onDelete} />)}
        </div>
      </div>
    </div>
  );
}
